import { useState, useCallback, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { VersionReportVersionEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { VersionReportTemplateEntity } from '../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { TemplateMenu } from './TemplateMenu'
import { ReportMenu } from './ReportMenu'
import { UseTemplateDataReturnValue } from '../../hooks/useTemplateData'
import { UseReportDataReturnValue } from '../../hooks/useReportData'
import { NewVersionDialog } from '../dialog/NewVersionDialog'
import { TemplateDialog } from '../dialog/TemplateDialog'
import { ReportDialog } from '../dialog/ReportDialog'
import Slide from '@mui/material/Slide'
import { styled } from '@mui/material/styles'
import { colorPalette } from '../../../../style/colorPalette'
import AlertDialog from '../../../../components/dialogs/AlertDialog'
import { intl } from '../../../../../i18n'
import { useVersionReportTemplateRepository } from '../../../../../services/adaptors/versionReportTemplateRepositoryAdaptor'
import { useVersionReportVersionRepository } from '../../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import { addGlobalMessage, MessageLevel } from '../../../../../store/messages'
import { getErrorCode } from '../../../../../infrastructure/api/error'
import { Text } from '../../../../components/typography'

interface VersionSelectMenuProps {
  projectUuid: string
  open: boolean
  onClose: () => void
  onReportSelect: (report: VersionReportVersionEntity) => void
  onAfterCreateReport: (uuid: string, isInitial: boolean) => void
  onAfterUpdateTemplate: () => Promise<void>
  onAfterUpdateReport: () => Promise<void>
  onAfterDeleteTemplate: (deletedTemplateUuid: string) => Promise<void>
  onAfterDeleteReport: (deletedReportUuid: string) => Promise<void>
  templateData: UseTemplateDataReturnValue
  reportData: UseReportDataReturnValue
}

export const VersionSelectMenu = ({
  projectUuid,
  open,
  onClose,
  onReportSelect,
  onAfterCreateReport,
  onAfterUpdateTemplate,
  onAfterUpdateReport,
  onAfterDeleteTemplate,
  onAfterDeleteReport,
  templateData,
  reportData,
}: VersionSelectMenuProps) => {
  const dispatch = useDispatch()
  const { deleteTemplate } = useVersionReportTemplateRepository()
  const { deleteVersion } = useVersionReportVersionRepository()
  const [isNewVersionDialogOpen, setIsNewVersionDialogOpen] =
    useState<boolean>(false)
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] =
    useState<boolean>(false)
  const [editingTemplate, setEditingTemplate] = useState<
    VersionReportTemplateEntity | undefined
  >()
  const [isReportDialogOpen, setIsReportDialogOpen] = useState<boolean>(false)
  const [editingReport, setEditingReport] = useState<
    VersionReportVersionEntity | undefined
  >()
  const [isDeleteTemplateDialogOpen, setIsDeleteTemplateDialogOpen] =
    useState<boolean>(false)
  const [deletingTemplate, setDeletingTemplate] = useState<
    VersionReportTemplateEntity | undefined
  >()
  const isDeletingTemplateRef = useRef<boolean>(false)
  const [isDeletingTemplate, setIsDeletingTemplate] = useState<boolean>(false)
  const [isDeleteReportDialogOpen, setIsDeleteReportDialogOpen] =
    useState<boolean>(false)
  const [deletingReport, setDeletingReport] = useState<
    VersionReportVersionEntity | undefined
  >()
  const isDeletingReportRef = useRef<boolean>(false)
  const [isDeletingReport, setIsDeletingReport] = useState<boolean>(false)

  const {
    selectTemplate: selectTemplateData,
    resetTemplateSelection,
    selectedTemplate,
    loading: templateLoading,
    templates,
    refreshTemplates,
  } = templateData

  const { reports, loading: reportLoading, refreshReports } = reportData

  const openNewVersionDialog = useCallback(() => {
    setIsNewVersionDialogOpen(true)
  }, [])

  const closeNewVersionDialog = useCallback(() => {
    setIsNewVersionDialogOpen(false)
  }, [])

  const openTemplateDialog = useCallback(() => {
    setIsTemplateDialogOpen(true)
  }, [])

  const closeTemplateDialog = useCallback(() => {
    setIsTemplateDialogOpen(false)
    setEditingTemplate(undefined)
  }, [])

  const handleAfterCreateReportInternal = useCallback(
    async (
      uuid: string,
      _name: string,
      isInitial: boolean,
      _version: VersionReportVersionEntity
    ) => {
      await refreshReports()
      await refreshTemplates()
      onAfterCreateReport(uuid, isInitial)
    },
    [onAfterCreateReport, refreshReports, refreshTemplates]
  )

  const handleTemplateUpdate = useCallback(
    (template: VersionReportTemplateEntity) => {
      setEditingTemplate(template)
      setIsTemplateDialogOpen(true)
    },
    []
  )

  const handleAfterSubmitTemplateInternal = useCallback(async () => {
    const wasEditing = editingTemplate !== undefined
    setIsTemplateDialogOpen(false)
    setEditingTemplate(undefined)
    await refreshTemplates()
    if (wasEditing) {
      await onAfterUpdateTemplate()
    }
  }, [editingTemplate, refreshTemplates, onAfterUpdateTemplate])

  const handleTemplateDeleteRequest = useCallback(
    (template: VersionReportTemplateEntity) => {
      setDeletingTemplate(template)
      setIsDeleteTemplateDialogOpen(true)
    },
    []
  )

  // 閉じるアニメーション中も内容を表示し続けるため、対象データは
  // ここではクリアせず次回オープン時に入れ替える
  const closeDeleteTemplateDialog = useCallback(() => {
    setIsDeleteTemplateDialogOpen(false)
  }, [])

  const handleConfirmDeleteTemplate = useCallback(async () => {
    if (!deletingTemplate || isDeletingTemplateRef.current) return
    isDeletingTemplateRef.current = true
    setIsDeletingTemplate(true)
    try {
      await deleteTemplate(deletingTemplate.uuid)
      setIsDeleteTemplateDialogOpen(false)
      await onAfterDeleteTemplate(deletingTemplate.uuid)
      await refreshTemplates()
      dispatch(
        addGlobalMessage({
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage(
            { id: 'versionReport.template.delete.success' },
            { name: deletingTemplate.name }
          ),
        })
      )
    } catch (error) {
      console.error('Template delete failed:', error)
      setIsDeleteTemplateDialogOpen(false)
      const errorCode = getErrorCode(error)
      if (errorCode === 'CONSISTENCY_VIOLATION' || errorCode === 'NOT_FOUND') {
        await refreshTemplates()
        dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({
              id: 'versionReport.template.delete.conflict',
            }),
          })
        )
      } else {
        throw error
      }
    } finally {
      isDeletingTemplateRef.current = false
      setIsDeletingTemplate(false)
    }
  }, [
    deletingTemplate,
    deleteTemplate,
    onAfterDeleteTemplate,
    refreshTemplates,
    dispatch,
  ])

  const handleReportUpdate = useCallback(
    (report: VersionReportVersionEntity) => {
      setEditingReport(report)
      setIsReportDialogOpen(true)
    },
    []
  )

  const handleReportDeleteRequest = useCallback(
    (report: VersionReportVersionEntity) => {
      setDeletingReport(report)
      setIsDeleteReportDialogOpen(true)
    },
    []
  )

  // 閉じるアニメーション中も内容を表示し続けるため、対象データは
  // ここではクリアせず次回オープン時に入れ替える
  const closeDeleteReportDialog = useCallback(() => {
    setIsDeleteReportDialogOpen(false)
  }, [])

  const handleConfirmDeleteReport = useCallback(async () => {
    if (!deletingReport || isDeletingReportRef.current) return
    isDeletingReportRef.current = true
    setIsDeletingReport(true)
    try {
      await deleteVersion(deletingReport.uuid)
      setIsDeleteReportDialogOpen(false)
      await onAfterDeleteReport(deletingReport.uuid)
      await refreshReports()
      await refreshTemplates()
      dispatch(
        addGlobalMessage({
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage(
            { id: 'versionReport.delete.success' },
            { name: deletingReport.name }
          ),
        })
      )
    } catch (error) {
      console.error('Report delete failed:', error)
      setIsDeleteReportDialogOpen(false)
      const errorCode = getErrorCode(error)
      if (errorCode === 'CONSISTENCY_VIOLATION' || errorCode === 'NOT_FOUND') {
        await refreshReports()
        await refreshTemplates()
        dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({
              id: 'versionReport.delete.conflict',
            }),
          })
        )
      } else {
        throw error
      }
    } finally {
      isDeletingReportRef.current = false
      setIsDeletingReport(false)
    }
  }, [
    deletingReport,
    deleteVersion,
    onAfterDeleteReport,
    refreshReports,
    refreshTemplates,
    dispatch,
  ])

  const closeReportDialog = useCallback(() => {
    setIsReportDialogOpen(false)
    setEditingReport(undefined)
  }, [])

  const handleAfterSubmitReportInternal = useCallback(async () => {
    setIsReportDialogOpen(false)
    setEditingReport(undefined)
    await refreshReports()
    await onAfterUpdateReport()
  }, [refreshReports, onAfterUpdateReport])

  const isReportMode = useMemo(() => !!selectedTemplate, [selectedTemplate])

  return (
    <>
      <StyledCollapse in={open} direction="right">
        <DialogContainer>
          {isReportMode ? (
            <ReportMenu
              templateName={selectedTemplate?.name || ''}
              reports={reports}
              loading={reportLoading}
              onReportSelect={onReportSelect}
              onReportUpdate={handleReportUpdate}
              onReportDelete={handleReportDeleteRequest}
              onBackToTemplate={resetTemplateSelection}
              onCreateNewReport={openNewVersionDialog}
              onClose={onClose}
            />
          ) : (
            <TemplateMenu
              templates={templates}
              loading={templateLoading}
              onTemplateSelect={selectTemplateData}
              onCreateNewTemplate={openTemplateDialog}
              onTemplateUpdate={handleTemplateUpdate}
              onTemplateDelete={handleTemplateDeleteRequest}
              onClose={onClose}
            />
          )}
        </DialogContainer>
      </StyledCollapse>
      <AlertDialog
        isOpen={isDeleteTemplateDialogOpen}
        title={intl.formatMessage({
          id: 'versionReport.template.delete.title',
        })}
        message={intl.formatMessage({
          id: 'versionReport.template.delete.message',
        })}
        extraContent={<DeleteCandidate name={deletingTemplate?.name ?? ''} />}
        submitButtonTitle={intl.formatMessage({
          id: 'versionReport.delete.submit',
        })}
        submitHandler={handleConfirmDeleteTemplate}
        closeHandler={closeDeleteTemplateDialog}
        loading={isDeletingTemplate}
      />
      <AlertDialog
        isOpen={isDeleteReportDialogOpen}
        title={intl.formatMessage({ id: 'versionReport.delete.title' })}
        message={intl.formatMessage({ id: 'versionReport.delete.message' })}
        extraContent={<DeleteCandidate name={deletingReport?.name ?? ''} />}
        submitButtonTitle={intl.formatMessage({
          id: 'versionReport.delete.submit',
        })}
        submitHandler={handleConfirmDeleteReport}
        closeHandler={closeDeleteReportDialog}
        loading={isDeletingReport}
      />
      <NewVersionDialog
        open={isNewVersionDialogOpen}
        templateUuid={selectedTemplate?.uuid}
        onAfterSubmit={handleAfterCreateReportInternal}
        onClose={closeNewVersionDialog}
      />
      <TemplateDialog
        open={isTemplateDialogOpen}
        onAfterSubmit={handleAfterSubmitTemplateInternal}
        onClose={closeTemplateDialog}
        original={editingTemplate}
      />
      {editingReport && (
        <ReportDialog
          open={isReportDialogOpen}
          onAfterSubmit={handleAfterSubmitReportInternal}
          onClose={closeReportDialog}
          original={editingReport}
        />
      )}
    </>
  )
}

type DeleteCandidateProps = {
  name: string
}

// PJ Plan画面のWBS削除ダイアログ（DeleteRowConfirmationDialog）と同じ見た目にするための
// シンプルな削除対象表示
const DeleteCandidate = ({ name }: DeleteCandidateProps) => (
  <CandidateList>
    <CandidateItem>
      <Text variant="s" color={colorPalette.monotone[9]}>
        {name}
      </Text>
    </CandidateItem>
  </CandidateList>
)

const CandidateList = styled('ul')({
  listStyle: 'none',
  margin: '16px 0 0',
  padding: '4px 8px',
  border: `1px solid ${colorPalette.monotone[3]}`,
  borderRadius: '5px',
  backgroundColor: colorPalette.monotone[1],
  wordWrap: 'break-word',
})

const CandidateItem = styled('li')({
  padding: '4px 6px',
})

const DialogContainer = styled('div')({
  width: '480px',
  height: '100%',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
})

// MUI Drawer 相当の z-index。PageContentArea の isolation: isolate により
// AG Grid 内部の z-index を超えてダイアログが最前面に表示されるようにする
const DIALOG_Z_INDEX = 1200

const StyledCollapse = styled(Slide)({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: DIALOG_Z_INDEX,
  height: '100%',
})
